import { useAppStore } from '@/store/appStore'
import { guardAction as guardActionApi } from '@/features/ksec/api'
import type { AccessAction } from '@/features/ksec/types'

/**
 * Returns helper functions to check whether the current user can see or
 * interact with a given tagCode, based on the access map loaded at bootstrap.
 */
export function useAccess() {
  const { access, identity } = useAppStore()

  /** True if the user has *any* access to this tagCode (map entry exists). */
  function canSee(tagCode: string): boolean {
    return !!access.map[tagCode]
  }

  /** True if the user can write/edit (RW or ALL). */
  function canWrite(tagCode: string): boolean {
    const action = access.map[tagCode]
    return action === 'RW' || action === 'ALL'
  }

  /** True if the user has full CRUD (ALL — Kashio employees). */
  function canAll(tagCode: string): boolean {
    return access.map[tagCode] === 'ALL'
  }

  /** Returns the raw action string, or undefined if no access. */
  function getAction(tagCode: string): AccessAction | undefined {
    return access.map[tagCode]
  }

  /**
   * Server-side guard for critical actions.
   * Calls POST /ksec/v2/auth/validate-access and returns allowed boolean.
   * Use before executing destructive mutations (create user, revoke permission, etc).
   */
  async function guard(tagCode: string, action: AccessAction = 'RW'): Promise<boolean> {
    return guardActionApi(identity.partyId, tagCode, action)
  }

  return { canSee, canWrite, canAll, getAction, guard }
}
